/**
 * SandboxEditorPanel — the thread's sandbox as a full VS Code (openvscode-server),
 * embedded. File tree, editor, search and terminal are VS Code's own, working on
 * the sandbox volume directly; see api ai-chat.editor.ts for how it is served.
 *
 * Opening the panel boots the sandbox when it is cold, so the panel polls
 * until it is up. When VS Code changes files (the pd-bridge extension reports
 * saves, file operations and finished terminal commands), the panel pings,
 * which carries the change into the workspace the chat reads. Nothing is sent
 * on a timer: that would keep the sandbox alive for as long as the tab is open.
 *
 * An open editor keeps its sandbox running (Modal counts the connection as
 * activity), so the panel lets go of it whenever nobody is there: the tab is
 * hidden for a minute, or two minutes pass with no use of VS Code (reported by
 * the extension) or of this page. It reconnects by itself when someone is back.
 * Closing the panel or switching chats unmounts it, which disconnects at once.
 *
 * The chat's own file actions ("Add to Chat", "Open in Reviewer") are VS Code
 * commands from the pd-bridge extension in the sandbox; the ones that need this
 * page arrive here as events from the editor's gate.
 */
import { useCallback, useEffect, useRef, useState, type FC, type RefObject } from 'react';
import { BookmarkCheck, Code2, Loader2, MessageSquarePlus, Minimize2, Pause, RotateCcw, X } from 'lucide-react';
import { api } from '@/services/api';
import { SavedWorkspacesPanel } from './SavedWorkspacesPanel';

type EditorStatus =
  | { state: 'unavailable'; reason: string }
  | {
      state: 'warming';
      restarting?: boolean;
      boot?: { progress?: { phase: string; seededFiles?: number; totalFiles?: number } };
    }
  | { state: 'restart-required' }
  | { state: 'failed'; error: string }
  | { state: 'ready'; url: string };

const POLL_MS = 3000;
/** No use of VS Code or of the page for this long: disconnect. */
const IDLE_DISCONNECT_MS = 2 * 60_000;
/** Tab hidden for this long: disconnect. */
const HIDDEN_DISCONNECT_MS = 60_000;

type UseInChat = (files: Array<{ name: string; path: string; url: string }>) => void;

interface SandboxEditorPanelProps {
  threadId: string;
  onClose: () => void;
  /** This thread has no sandbox to edit in; the file tree is all there is. */
  onUnavailable: () => void;
  onUseInChat?: UseInChat;
  /** Start a fresh chat seeded with a copy of this workspace. */
  onNewThreadWithWorkspace?: () => void;
  newThreadBusy?: boolean;
  /**
   * The chat column. An opened file floats over it instead of squeezing it,
   * leaving a strip of the chat in view.
   */
  chatColumnRef: RefObject<HTMLElement | null>;
}

/** How much of the chat stays visible beside an open file. */
function chatStripWidth(chatWidth: number): number {
  return Math.max(260, Math.round(chatWidth * 0.3));
}

export const SandboxEditorPanel: FC<SandboxEditorPanelProps> = ({
  threadId,
  onClose,
  onUnavailable,
  onUseInChat,
  onNewThreadWithWorkspace,
  newThreadBusy,
  chatColumnRef,
}) => {
  const [status, setStatus] = useState<EditorStatus | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [copyPending, setCopyPending] = useState(false);
  // Bumped to ask again after a restart or a failure.
  const [attempt, setAttempt] = useState(0);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  pausedRef.current = paused;
  const lastActiveRef = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ask = async () => {
      try {
        const next = await api.post<EditorStatus>(`/chat/threads/${threadId}/editor`, {}, 58_000);
        if (cancelled) return;
        setRequestError(null);
        setStatus(next);
        if (next.state === 'warming') timer = setTimeout(ask, POLL_MS);
        if (next.state === 'unavailable') onUnavailable();
      } catch (err) {
        if (!cancelled) setRequestError(err instanceof Error ? err.message : String(err));
      }
    };
    setStatus(null);
    ask();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [threadId, attempt, onUnavailable]);

  const url = status?.state === 'ready' && !paused ? status.url : null;

  // VS Code starts in its default layout and the extension then reduces it to
  // the file tree; keep it covered until that is done (or plainly late).
  const [layoutReady, setLayoutReady] = useState(false);
  // A file is open: the editor floats over the chat, with the tree on its left.
  const [filesOpen, setFilesOpen] = useState(false);
  useEffect(() => {
    setLayoutReady(false);
    setFilesOpen(false);
    if (!url) return;
    const late = setTimeout(() => setLayoutReady(true), 15_000);
    return () => clearTimeout(late);
  }, [url]);

  // Where the floating editor sits: from a strip into the chat to the right
  // edge, below the panel header. Kept current while it is open.
  const contentRef = useRef<HTMLDivElement>(null);
  const floatRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [floatBox, setFloatBox] = useState<{ left: number; top: number } | null>(null);
  useEffect(() => {
    if (!filesOpen) { setFloatBox(null); return; }
    const place = () => {
      const chat = chatColumnRef.current?.getBoundingClientRect();
      const content = contentRef.current?.getBoundingClientRect();
      if (!chat || !content) return;
      setFloatBox({ left: Math.round(chat.left + chatStripWidth(chat.width)), top: Math.round(content.top) });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [filesOpen, chatColumnRef]);

  // A video asked for full screen. Its player sits inside VS Code's webview
  // frames, which do not allow it, so the page shows the same player over
  // everything and makes that full screen. The click that asked counts as one
  // on this page too, which is what lets it.
  const [fullscreenVideo, setFullscreenVideo] = useState<string | null>(null);
  const fullscreenRef = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    if (!fullscreenVideo) return;
    fullscreenRef.current?.requestFullscreen?.().catch(() => { /* stays a full-window overlay */ });
    const onChange = () => { if (!document.fullscreenElement) setFullscreenVideo(null); };
    const onMessage = (e: MessageEvent) => { if (e.data?.pdVideo === 'close') setFullscreenVideo(null); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.fullscreenElement) setFullscreenVideo(null); };
    document.addEventListener('fullscreenchange', onChange);
    window.addEventListener('message', onMessage);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      window.removeEventListener('message', onMessage);
      window.removeEventListener('keydown', onKey);
    };
  }, [fullscreenVideo]);

  // Folding back is VS Code closing its files (auto-save has written them);
  // the extension then reports the tree-only layout and the float goes away.
  const closeFiles = useCallback(() => {
    if (!url) return;
    fetch(`${url}__pd/to-editor`, { method: 'POST', body: JSON.stringify({ action: 'closeEditors' }) }).catch(() => {});
  }, [url]);

  useEffect(() => {
    if (!filesOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeFiles(); };
    // Clicks inside VS Code never reach this document; one that does landed
    // outside the editor — on the chat strip, say.
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (floatRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      closeFiles();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [filesOpen, closeFiles]);

  // Someone is here: reconnect if paused, else push the idle deadline back.
  const present = useCallback(() => {
    lastActiveRef.current = Date.now();
    if (pausedRef.current) {
      setPaused(false);
      setAttempt((n) => n + 1);
    }
  }, []);

  // Use of the page itself counts (typing to the agent while watching the
  // files), and so does coming back to the tab.
  useEffect(() => {
    let last = 0;
    const onInput = () => {
      if (Date.now() - last < 5000) return;
      last = Date.now();
      present();
    };
    let hiddenTimer: ReturnType<typeof setTimeout> | undefined;
    const onVisibility = () => {
      clearTimeout(hiddenTimer);
      if (document.visibilityState === 'visible') present();
      else hiddenTimer = setTimeout(() => setPaused(true), HIDDEN_DISCONNECT_MS);
    };
    const events = ['pointermove', 'pointerdown', 'keydown', 'wheel'] as const;
    events.forEach((e) => window.addEventListener(e, onInput, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      events.forEach((e) => window.removeEventListener(e, onInput));
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(hiddenTimer);
    };
  }, [present]);

  useEffect(() => {
    if (!url) return;
    lastActiveRef.current = Date.now();
    const id = setInterval(() => {
      if (Date.now() - lastActiveRef.current > IDLE_DISCONNECT_MS) setPaused(true);
    }, 15_000);
    return () => clearInterval(id);
  }, [url]);

  // A copy from the chat this one was forked from is still landing.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const { copyPending: pending } = await api.get<{ copyPending: boolean }>(`/chat/threads/${threadId}/workspace/status`);
        if (cancelled) return;
        setCopyPending(pending);
        if (pending) timer = setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setCopyPending(false);
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [threadId]);

  // Kept in a ref so a new callback identity does not reconnect the stream.
  const useInChatRef = useRef(onUseInChat);
  useInChatRef.current = onUseInChat;

  useEffect(() => {
    if (!url) return;
    const events = new EventSource(`${url}__pd/events?origin=${encodeURIComponent(window.location.origin)}`);
    events.onmessage = async (e) => {
      let msg: { action?: string; paths?: string[] };
      try { msg = JSON.parse(e.data); } catch { return; }
      lastActiveRef.current = Date.now();
      if (msg.action === 'active') return;
      if (msg.action === 'layout') {
        setLayoutReady(true);
        setFilesOpen(!!(msg as { editorOpen?: boolean }).editorOpen);
        return;
      }
      if (msg.action === 'download') {
        // A folder as a zip, streamed by the gate. Started from this page: the
        // editor's frame is not allowed to download it the way VS Code tries to.
        const zip = (msg as { zip?: string }).zip;
        if (zip === undefined) return;
        const a = document.createElement('a');
        a.href = `${url}__pd/zip/${zip}`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      if (msg.action === 'videoFullscreen') {
        const v = msg as { path?: string; time?: number; playing?: boolean };
        if (v.path) setFullscreenVideo(`${url}__pd/player/${v.path}?fs=1&t=${v.time ?? 0}&p=${v.playing ? 1 : 0}`);
        return;
      }
      if (msg.action === 'changed') {
        api.post(`/chat/threads/${threadId}/editor/ping`).catch(() => {});
        return;
      }
      if (msg.action !== 'addToChat' || !msg.paths?.length || !useInChatRef.current) return;
      // VS Code saved straight to the volume; carry it into the workspace the
      // chat reads before pointing the chat at it.
      await api.get(`/chat/threads/${threadId}/workspace`, { reconcile: '1' }).catch(() => {});
      const files = await Promise.all(msg.paths.map(async (path) => {
        const url = await api
          .get<{ url: string }>(`/chat/threads/${threadId}/workspace/download`, { path })
          .then((r) => r.url, () => '');
        return { name: path.split('/').pop() || path, path, url };
      }));
      useInChatRef.current(files);
    };
    return () => events.close();
  }, [threadId, url]);

  const restart = useCallback(async () => {
    try {
      await api.post(`/chat/threads/${threadId}/editor/restart`);
      setAttempt((n) => n + 1);
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : String(err));
    }
  }, [threadId]);

  return (
    <div ref={panelRef} className="flex flex-col h-full bg-white border-l border-zinc-200">
      {fullscreenVideo && (
        <iframe
          ref={fullscreenRef}
          src={fullscreenVideo}
          title="Video"
          allow="fullscreen; autoplay"
          className="fixed inset-0 z-[100] w-screen h-screen border-0 bg-black"
        />
      )}
      <div className="flex h-12 items-center justify-between px-3 border-b border-zinc-200 bg-white shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Code2 className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
          <span className="text-sm font-medium text-zinc-900">Workspace</span>
          {copyPending && (
            <span className="flex items-center gap-1 text-xs text-zinc-600 bg-zinc-100 px-1.5 py-0.5 rounded-md" title="Copying workspace from the source chat">
              <Loader2 className="w-2.5 h-2.5 animate-spin" /> copying
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <button onClick={() => setShowSaved((v) => !v)} className={`p-1.5 rounded-md transition-colors ${showSaved ? 'bg-zinc-100 text-zinc-600' : 'hover:bg-zinc-200'}`} title="Saved Workspaces">
            <BookmarkCheck className={`w-3.5 h-3.5 ${showSaved ? 'text-zinc-600' : 'text-zinc-500'}`} />
          </button>
          {onNewThreadWithWorkspace && (
            <button onClick={onNewThreadWithWorkspace} disabled={newThreadBusy} className="p-1.5 rounded-md hover:bg-zinc-100 transition-colors disabled:opacity-50" title="Start a new chat with this workspace">
              {newThreadBusy
                ? <Loader2 className="w-3.5 h-3.5 text-zinc-500 animate-spin" />
                : <MessageSquarePlus className="w-3.5 h-3.5 text-zinc-500" />}
            </button>
          )}
          {filesOpen && (
            <button onClick={closeFiles} className="p-1.5 rounded-md bg-zinc-100 hover:bg-zinc-100 transition-colors" title="Close open files (Esc)">
              <Minimize2 className="w-3.5 h-3.5 text-zinc-600" />
            </button>
          )}
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-zinc-100 transition-colors" title="Close">
            <X className="w-3.5 h-3.5 text-zinc-500" />
          </button>
        </div>
      </div>

      {showSaved && (
        <SavedWorkspacesPanel
          threadId={threadId}
          onClose={() => setShowSaved(false)}
          onLoaded={() => {}}
          onError={setNotice}
        />
      )}
      {notice && (
        <div className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-red-600 bg-red-50 border-b border-red-100 shrink-0">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="p-0.5 rounded hover:bg-red-100"><X className="w-3 h-3" /></button>
        </div>
      )}

      <div ref={contentRef} className="flex-1 min-h-0 relative">
        {url ? (
          <>
            {/* One element either way: moving the iframe in the DOM would reload VS Code. */}
            <div
              ref={floatRef}
              className={floatBox
                ? 'fixed right-0 bottom-0 z-40 bg-white border-l border-t border-zinc-200 shadow-lg rounded-tl-lg overflow-hidden'
                : 'absolute inset-0'}
              style={floatBox ? { left: floatBox.left, top: floatBox.top } : undefined}
            >
              <iframe
                key={url}
                src={url}
                title="VS Code"
                className={`w-full h-full border-0 ${layoutReady ? '' : 'invisible'}`}
                allow="clipboard-read; clipboard-write"
              />
            </div>
            {!layoutReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-white">
                <Loader2 className="w-5 h-5 text-zinc-600 animate-spin" />
              </div>
            )}
          </>
        ) : (
          <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            {paused ? (
              <>
                <Pause className="w-5 h-5 text-zinc-300" />
                <p className="text-xs text-zinc-400">Paused while you&apos;re away. Reconnects when you&apos;re back.</p>
              </>
            ) : (
              <EditorPlaceholder status={status} requestError={requestError} onRestart={restart} onRetry={() => setAttempt((n) => n + 1)} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const EditorPlaceholder: FC<{
  status: EditorStatus | null;
  requestError: string | null;
  onRestart: () => void;
  onRetry: () => void;
}> = ({ status, requestError, onRestart, onRetry }) => {
  const button = 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-zinc-100 text-zinc-700 hover:bg-zinc-100 transition-colors';

  if (requestError || status?.state === 'failed') {
    return (
      <>
        <p className="text-xs text-red-600 max-w-sm break-words">{requestError ?? (status as { error: string }).error}</p>
        <button onClick={onRetry} className={button}><RotateCcw className="w-3.5 h-3.5" /> Try again</button>
      </>
    );
  }
  if (status?.state === 'unavailable') return <p className="text-xs text-zinc-500">{status.reason}</p>;
  if (status?.state === 'restart-required') {
    return (
      <>
        <p className="text-xs text-zinc-600 max-w-sm">
          This chat&apos;s sandbox started before the editor existed, so it has to be restarted once.
          Files are kept; anything running in the sandbox right now is stopped.
        </p>
        <button onClick={onRestart} className={button}><RotateCcw className="w-3.5 h-3.5" /> Restart sandbox</button>
      </>
    );
  }

  const progress = status?.state === 'warming' ? status.boot?.progress : undefined;
  const label =
    status?.state === 'warming' && status.restarting ? 'Restarting sandbox…'
    : progress?.phase === 'seeding' && progress.totalFiles ? `Copying files into the sandbox… ${progress.seededFiles ?? 0}/${progress.totalFiles}`
    : status?.state === 'warming' ? 'Starting sandbox…'
    : status === null ? 'Opening…'
    : 'Starting editor…';
  return (
    <>
      <Loader2 className="w-5 h-5 text-zinc-600 animate-spin" />
      <p className="text-xs text-zinc-500">{label}</p>
    </>
  );
};
